import React, { useMemo, useCallback, useState, FC } from "react";
import _ from "lodash";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { defaultTableRowRenderer } from "react-virtualized/dist/commonjs/index";
import { Table, TableRowProps, TableCellProps, Column, AutoSizer } from "react-virtualized";
import "react-virtualized/styles.css";
import IconButton from "@material-ui/core/IconButton";
import { format } from "lib/globalize-util";
import theme from "styles/theme";
import classNames from "classnames";
import HoverListener from "components/hover-listener";
import { ITableColumn } from "constants/table-types";
import { InlineLoading } from "components/content-loading";
import TrashIcon from "components/icons/trash-new";
import { styles } from "components/table/styles";
import DragIcon from "material-ui/svg-icons/editor/drag-handle";
import { useDebounceFunction as useDebounce } from "hooks";
import { imageFallback } from "helpers/product-helpers";
import Image from "components/image";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import VerticalAlignTopIcon from "@material-ui/icons/VerticalAlignTop";
import VerticalAlignBottomIcon from "@material-ui/icons/VerticalAlignBottom";
import { SortableContainer, SortableElement, SortableHandle } from "react-sortable-hoc";
import { MEDIA_TYPE_VIDEO, MEDIA_TYPE_IMAGE, MEDIA_TYPE_360, MEDIA_TYPE_THREEDIUM } from "constants/media";
import { IMediaAsset } from "constants/media-asset";
import { DATATEST } from "../../constants/integration";
import Image3DIcon from "../icons/image-3d";
import { blurFix } from "styles/mixins";

const TABLE_HEADER_HEIGHT = 48;
const TABLE_ROW_HEIGHT = 60;

interface IProps {
    media: IMediaAsset[];
    isFetching: boolean;
    touchDevice: boolean;
    onDelete?: (index: number) => void;
    onUpdateMediaIndex?: (oldIndex: number, newIndex: number) => void;
}

const DragHandle = SortableHandle(() => (
    <DragIcon data-test={DATATEST.MEDIA.MANAGE_MEDIA_TABLE.DRAG_HANDLE} style={{ zIndex: 99 }} />
));
const SortableTableRowRenderer = SortableElement((props: { rowProps: TableRowProps }) =>
    defaultTableRowRenderer(props.rowProps)
);
const SortableTable = SortableContainer(Table);

export const ManageMediaTable: FC<IProps> = (props: IProps) => {
    const { media, isFetching, onDelete, onUpdateMediaIndex, ...rest } = props;
    const { touchDevice } = props;

    const classes = useStyles();

    const [hoverIndex, setHoverIndex] = useState<number | null>(null);

    const COLUMNS: ITableColumn[] = useMemo(
        () => [
            {
                menuLabel: "dragHandle",
                dataKey: "dragHandle",
                width: 50,
                key: "dragHandle",
                flexGrow: 0,
                flexShrink: 0,
            },
            { menuLabel: "image", dataKey: "source", width: 60, key: "image", flexGrow: 0, flexShrink: 0 },
            { label: format("Media type"), dataKey: "type", width: 150, key: "mediaType", flexGrow: 3, flexShrink: 3 },
            { label: format("Position"), dataKey: "key", width: 100, key: "key", flexGrow: 0, flexShrink: 0 },
            { menuLabel: "actions", dataKey: "actions", width: 250, key: "actions", flexGrow: 3, flexShrink: 3 },
        ],
        []
    );

    const mediaTypeTranslations = useMemo(
        () => ({
            [MEDIA_TYPE_IMAGE]: format("Image"),
            [MEDIA_TYPE_VIDEO]: format("Video"),
            [MEDIA_TYPE_360]: format("Image 360"),
            [MEDIA_TYPE_THREEDIUM]: format("3D"),
        }),
        []
    );

    const rowCount = useMemo<number>(() => media.length || 1, [media]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fullWidth = useMemo(() => _.sumBy(COLUMNS, (column) => column.width + 10), []);

    const setHoverIndexDebounced = useDebounce(setHoverIndex, 100);

    const handleDeleteMedia = useCallback(
        (index: number) => {
            setHoverIndexDebounced(null);
            onDelete(index);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onDelete]
    );

    const rowGetter = useCallback(({ index }) => media[index], [media]);

    const noRowRenderer = useCallback(
        () =>
            isFetching ? (
                <InlineLoading className={classes.loading} />
            ) : (
                <div data-test={DATATEST.MEDIA.NO_RESULTS_MESSAGE} className={classes.noResults}>
                    {format("No results found")}
                </div>
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isFetching]
    );

    const rowRenderer = useCallback(
        (rowProps: TableRowProps) => {
            const showLoader = isFetching && rowProps.index === rowCount - 1;
            if (showLoader) {
                return (
                    <div key="loading" style={rowProps.style}>
                        <InlineLoading />
                    </div>
                );
            } else if (rowProps.rowData) {
                if (touchDevice) {
                    return defaultTableRowRenderer(rowProps);
                } else {
                    return (
                        <HoverListener key={rowProps.key} debounce={100}>
                            {({ hover }) => {
                                if ((hover && hoverIndex === null) || (!hover && hoverIndex === rowProps.index)) {
                                    setHoverIndexDebounced(hover ? rowProps.index : null);
                                }
                                return <SortableTableRowRenderer index={rowProps.index} rowProps={rowProps} />;
                            }}
                        </HoverListener>
                    );
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isFetching, touchDevice, hoverIndex, rowCount]
    );

    const cellRenderer = useCallback(
        (cellProps: TableCellProps) => {
            const { dataKey, rowData, rowIndex } = cellProps;
            switch (dataKey) {
                case "dragHandle":
                    return <DragHandle />;
                case "source":
                    return (
                        <div
                            data-test={
                                rowData.emptyMedia
                                    ? DATATEST.MEDIA.MANAGE_MEDIA_TABLE.MEDIA_IMAGE_PLACEHOLDER
                                    : DATATEST.MEDIA.MANAGE_MEDIA_TABLE.MEDIA_IMAGE
                            }
                        >
                            <MediaImage type={rowData.type} images={[_.get(rowData, dataKey), imageFallback]} />
                        </div>
                    );
                case "type":
                    return (
                        <div data-test={DATATEST.MEDIA.MANAGE_MEDIA_TABLE.MEDIA_TYPE}>
                            {mediaTypeTranslations[_.get(rowData, dataKey, "--")]}
                        </div>
                    );
                case "key":
                    return (
                        <div data-test={DATATEST.MEDIA.MANAGE_MEDIA_TABLE.MEDIA_POSITION}>
                            {_.get(rowData, dataKey, "--")}
                        </div>
                    );
                case "actions":
                    return touchDevice || hoverIndex === rowIndex ? (
                        <TableActions
                            rowIndex={rowIndex}
                            lastIndex={media.length - 1}
                            onUpdateMediaIndex={onUpdateMediaIndex}
                            onDelete={handleDeleteMedia}
                        />
                    ) : null;
                default:
                    return _.get(rowData, dataKey, "--");
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [media, touchDevice, hoverIndex, onUpdateMediaIndex]
    );

    const onSortEnd = useCallback(
        ({ oldIndex, newIndex }) => {
            onUpdateMediaIndex(oldIndex, newIndex);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [media, onUpdateMediaIndex]
    );

    return (
        <AutoSizer className={classes.contentRoot}>
            {({ width }) => (
                <SortableTable
                    className={classes.table}
                    // sort props
                    helperClass={classes.helper}
                    onSortEnd={onSortEnd}
                    // table props
                    autoHeight={true}
                    useDragHandle={true}
                    height={TABLE_ROW_HEIGHT * rowCount}
                    width={Math.max(width, fullWidth)}
                    headerHeight={TABLE_HEADER_HEIGHT}
                    rowHeight={TABLE_ROW_HEIGHT}
                    rowCount={media.length}
                    rowGetter={rowGetter}
                    rowRenderer={rowRenderer}
                    noRowsRenderer={noRowRenderer}
                    {...rest}
                >
                    {_.map(COLUMNS, (col) => (
                        <Column {...col} key={col.dataKey} minWidth={col.width} cellRenderer={cellRenderer} />
                    ))}
                </SortableTable>
            )}
        </AutoSizer>
    );
};

const MediaImage: FC<{ type: string; images: string[] }> = ({ type, images }) => {
    const classes = useMediaImageStyles();
    return (
        <picture
            data-test={DATATEST.MEDIA.MANAGE_MEDIA_TABLE.MEDIA_SOURCE_CONTAINER}
            className={classes.imageContainer}
        >
            {type === MEDIA_TYPE_THREEDIUM && (
                <Image3DIcon data-test={DATATEST.MEDIA.MANAGE_MEDIA_TABLE.IMAGE_3D_ICON} className={classes.icon} />
            )}
            {type !== MEDIA_TYPE_THREEDIUM && <Image className={classes.image} src={images} alt="Product" />}
        </picture>
    );
};

const useMediaImageStyles = makeStyles({
    imageContainer: {
        display: "flex",
        opacity: 1,
        transition: "all 250ms cubic-bezier(0.23, 1, 0.32, 1) 0ms",
        "&.hover": {
            opacity: 0,
        },
        justifyContent: "center",
    },
    icon: {
        width: "26px !important",
        height: "26px !important",
        backgroundColor: "rgba(245,245,245,0.7)",
        borderRadius: "50%",
        color: theme.palette.grey,
    },
    image: {
        maxHeight: 60,
        maxWidth: 60,
        margin: "auto",
    },
});

interface ITableActions {
    rowIndex: number;
    lastIndex: number;
    onUpdateMediaIndex: (rowIndex: number, newIndex: number) => void;
    onDelete: (index: number) => void;
}

const TableActions: FC<ITableActions> = (props: ITableActions) => {
    const { rowIndex, lastIndex, onUpdateMediaIndex, onDelete } = props;

    const classes = useActionsStyles();

    const getClassnameDisabled = useCallback(
        (disabled: boolean) => (disabled ? classNames(classes.icon, classes.iconDisabled) : classes.icon),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleUpdate = useCallback(
        (rowIndex: number, newIndex: number) => () => onUpdateMediaIndex(rowIndex, newIndex),
        [onUpdateMediaIndex]
    );
    const handleDelete = useCallback((index: number) => () => onDelete(index), [onDelete]);

    const actions = useMemo(
        () => [
            {
                key: "up",
                dataTest: DATATEST.MEDIA.MANAGE_MEDIA_TABLE.ACTIONS.MOVE_UP,
                icon: <ArrowUpwardIcon className={getClassnameDisabled(rowIndex === 0)} />,
                disabled: rowIndex === 0,
                callback: handleUpdate(rowIndex, rowIndex - 1),
            },
            {
                key: "down",
                dataTest: DATATEST.MEDIA.MANAGE_MEDIA_TABLE.ACTIONS.MOVE_DOWN,
                icon: <ArrowDownwardIcon className={getClassnameDisabled(rowIndex === lastIndex)} />,
                disabled: rowIndex === lastIndex,
                callback: handleUpdate(rowIndex, rowIndex + 1),
            },
            {
                key: "top",
                dataTest: DATATEST.MEDIA.MANAGE_MEDIA_TABLE.ACTIONS.MOVE_TOP,
                icon: <VerticalAlignTopIcon className={getClassnameDisabled(rowIndex === 0)} />,
                disabled: rowIndex === 0,
                callback: handleUpdate(rowIndex, 0),
            },
            {
                key: "bottom",
                dataTest: DATATEST.MEDIA.MANAGE_MEDIA_TABLE.ACTIONS.MOVE_BOTTOM,
                icon: <VerticalAlignBottomIcon className={getClassnameDisabled(rowIndex === lastIndex)} />,
                disabled: rowIndex === lastIndex,
                callback: handleUpdate(rowIndex, lastIndex),
            },
            {
                key: "delete",
                dataTest: DATATEST.MEDIA.MANAGE_MEDIA_TABLE.ACTIONS.DELETE,
                icon: <TrashIcon className={classNames(classes.icon, classes.iconError)} />,
                callback: handleDelete(rowIndex),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rowIndex, lastIndex, handleUpdate, handleDelete]
    );

    return (
        <>
            {_.map(actions, (action) => (
                <IconButton
                    data-test={action.dataTest}
                    key={action.key}
                    disabled={action.disabled}
                    onClick={action.callback}
                >
                    {action.icon}
                </IconButton>
            ))}
        </>
    );
};

const useStyles = makeStyles({
    table: styles,
    contentRoot: {
        minHeight: "400px",
    },
    loading: {
        margin: "40px 0 0 0",
    },
    helper: {
        zIndex: "1500 !important" as any,
    },
    noResults: {
        textAlign: "center",
        maxWidth: 450,
        fontSize: 20,
        padding: "20px 10px",
        margin: "auto",
    },
    imageContainer: {
        display: "flex",
        opacity: 1,
        transition: "all 250ms cubic-bezier(0.23, 1, 0.32, 1) 0ms",
        "&.hover": {
            opacity: 0,
        },
    },
    image: {
        maxHeight: 60,
        maxWidth: 60,
        margin: "auto",
        ...blurFix,
    },
});

const useActionsStyles = makeStyles({
    icon: {
        width: 24,
        height: 24,
    },
    iconDisabled: {
        color: theme.palette.grey,
    },
    iconError: {
        color: theme.palette.error,
    },
});
